export interface CategoryNavItem {
    slug: string;
    name: string;
    bn: string;
}

export interface CategoryMeta {
    bn: string;
    color: string;
    description: string;
}

const CATEGORY_BN: Record<string, string> = {
    'bangladesh': 'বাংলাদেশ',
    'politics': 'রাজনীতি',
    'world': 'আন্তর্জাতিক',
    'business': 'অর্থনীতি',
    'sports': 'খেলা',
    'entertainment': 'বিনোদন',
    'opinion': 'মতামত',
    'technology': 'প্রযুক্তি',
    'lifestyle': 'জীবনযাপন',
    'education': 'শিক্ষা',
    'health': 'স্বাস্থ্য',
    'religion': 'ধর্ম',
    'chakri': 'চাকরি',
    'onnoalo': 'অন্য আলো',
    'durporobash': 'দূরপ্রবাস',
    'activities': 'কার্যক্রম',
    'fun': 'আমোদ',
    'reader': 'পাঠক',
    'ayojon': 'আয়োজন',
    'documents': 'নথিপত্র',
    'ampstories': 'ক্ষণচিত্র',
    'travel': 'ভ্রমণ',
    'arts': 'শিল্পকলা',
    'media': 'গণমাধ্যম',
    'hajj': 'হজ',
};

const CATEGORY_COLORS: Record<string, string> = {
    'bangladesh': '#C60000',
    'politics': '#1E293B',
    'world': '#2563EB',
    'business': '#059669',
    'sports': '#D97706',
    'entertainment': '#DB2777',
    'opinion': '#6366F1',
    'technology': '#0891B2',
    'lifestyle': '#84CC16',
    'education': '#F59E0B',
    'health': '#10B981',
    'religion': '#7C3AED',
    'chakri': '#0EA5E9',
};

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
    'bangladesh': 'বাংলাদেশের সর্বশেষ খবর ও ঘটনাপ্রবাহ',
    'politics': 'রাজনৈতিক সংবাদ ও বিশ্লেষণ',
    'world': 'বিশ্বের বিভিন্ন প্রান্তের খবর',
    'business': 'অর্থনৈতিক সংবাদ ও বাজার পরিস্থিতি',
    'sports': 'ক্রীড়া জগতের সব খবর',
    'entertainment': 'বিনোদন জগতের সর্বশেষ',
    'opinion': 'মতামত ও বিশ্লেষণ',
    'technology': 'প্রযুক্তি ও বিজ্ঞান সংবাদ',
    'lifestyle': 'জীবনযাপন ও সংস্কৃতি সংক্রান্ত খবর',
    'education': 'শিক্ষা খাতের সর্বশেষ সংবাদ',
    'health': 'স্বাস্থ্য ও চিকিৎসা সংক্রান্ত খবর',
    'religion': 'ধর্ম ও আধ্যাত্মিক বিষয়ের খবর',
    'chakri': 'চাকরি ও কর্মসংস্থান সংক্রান্ত খবর',
};

const PRIMARY_NAV_SLUGS = [
    'bangladesh',
    'politics',
    'world',
    'business',
    'sports',
    'entertainment',
    'opinion',
    'technology',
] as const;

const SECONDARY_NAV_SLUGS = [
    'lifestyle',
    'education',
    'health',
    'religion',
    'chakri',
    'onnoalo',
    'durporobash',
    'activities',
    'fun',
    'reader',
    'ayojon',
    'documents',
    'ampstories',
    'travel',
    'arts',
    'media',
    'hajj',
] as const;

export const BLOCKED_CATEGORY_SLUGS = new Set(['crime', 'war-crimes', 'roundtable']);

function toNavItem(slug: string): CategoryNavItem {
    return {
        slug,
        name: CATEGORY_BN[slug],
        bn: CATEGORY_BN[slug],
    };
}

export function normalizeCategorySlug(rawSlug: string): string {
    return rawSlug.trim().toLowerCase().replace(/\s+/g, '-');
}

export function isBlockedCategory(rawSlug: string): boolean {
    return BLOCKED_CATEGORY_SLUGS.has(normalizeCategorySlug(rawSlug));
}

/**
 * True when this site actually publishes the slug as a section.
 *
 * Used to tell a typo or crawler-probed slug apart from a real category that
 * merely has no articles yet. Not a complete picture on its own: two stored
 * categories are absent from `CATEGORY_BN` (`war-crimes`, which is blocked
 * anyway, and a 2-article Bengali-named section), so callers must combine this
 * with an article count before returning a 404.
 */
export function isKnownCategory(rawSlug: string): boolean {
    return Object.prototype.hasOwnProperty.call(
        CATEGORY_BN,
        normalizeCategorySlug(rawSlug),
    );
}

function titleCaseSlug(slug: string): string {
    return slug
        .split('-')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export function getCategoryLabelBn(rawSlug: string): string {
    const key = normalizeCategorySlug(rawSlug);
    if (isBlockedCategory(key)) {
        return titleCaseSlug(key);
    }
    if (CATEGORY_BN[key]) {
        return CATEGORY_BN[key];
    }
    return titleCaseSlug(key);
}

export function getCategoryMeta(slug: string): CategoryMeta {
    const key = normalizeCategorySlug(slug);
    return {
        bn: getCategoryLabelBn(slug),
        color: CATEGORY_COLORS[key] || '#1E293B',
        description: CATEGORY_DESCRIPTIONS[key] || 'এই বিভাগের সকল সংবাদ',
    };
}

const FOOTER_NAV_SLUGS = [
    'bangladesh',
    'politics',
    'world',
    'business',
] as const;

export const PRIMARY_NAV_CATEGORIES: CategoryNavItem[] = PRIMARY_NAV_SLUGS.map(toNavItem);

export const FOOTER_NAV_CATEGORIES: CategoryNavItem[] = FOOTER_NAV_SLUGS.map(toNavItem);

export const SECONDARY_NAV_CATEGORIES: CategoryNavItem[] = SECONDARY_NAV_SLUGS.map(toNavItem);
